import { getAccessibleGroups } from "../../WireGuardTunnels/wireGuardGroupUtils";
import React from "react";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  fetchAlarmReceivers,
  describeRequestFailure,
  fetchAlertsReceived,
  fetchCameraLastAlarms,
  fetchCameraLiveness,
  fetchSavedLoginStatus,
  fetchSiteCameraNames,
  fetchSiteCameras,
  fetchRecordingServers,
  fetchSiteChanges,
  fetchAllWireGuardTunnels,
  fetchParentAccounts,
  fetchReceiverTests,
  fetchSiteConnectionDetail,
  fetchSiteToSiteConnections,
  fetchSmtpFilenames,
  fetchSmtpInstructions,
  fetchSmtpAccountIds,
  fetchSmtpSiteIdsResult,
  fetchWireGuardGatewaySiteIds,
  fetchTroubleshootingSitesResult,
  fetchWireGuardTelemetry,
} from "./troubleshootingApi";
import { buildFindings } from "./buildFindings";
import { DEFAULT_CHANGES_WINDOW } from "./changesWindow";
import { ensureSession, withSessionRecovery } from "./authRecovery";
import { scopedSitesFor, scopedTunnelsFor, siteCameraHosts } from "./useScope";
import { FilenameRuleSummary } from "../tabs/SmtpFilenameDistribution";
import { summarizeFilenames } from "./smtpFilenameRule";
import {
  SAMPLE_SAVED_LOGIN_STATUS,
  SAMPLE_SITE_DETAIL,
  SAMPLE_TUNNELS,
} from "./troubleshootingSampleData";

export const AUTO_REFRESH_MS = 60_000;
/** A test alarm's history row can land a little after the test returns. */
export const RECEIVER_TESTS_RECHECK_MS = 15_000;

/**
 * Re-reads the test alarm history now and once more shortly after (K-13),
 * so a test just sent shows up without waiting for the minute refresh.
 */
export const useReloadReceiverTests = () => {
  const sampleData = useTroubleshootingStore((s) => s.sampleData);
  const setReceiverTests = useTroubleshootingStore((s) => s.setReceiverTests);
  const timer = React.useRef<number | null>(null);
  const mounted = React.useRef(true);
  React.useEffect(
    () => () => {
      mounted.current = false;
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    []
  );
  return React.useCallback(() => {
    const load = () =>
      fetchReceiverTests(sampleData).then((r) => {
        if (mounted.current) setReceiverTests(r);
      });
    void load();
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      void load();
    }, RECEIVER_TESTS_RECHECK_MS);
  }, [sampleData, setReceiverTests]);
};

/** Empty when every account loaded. Otherwise says how much of the site list is missing. */
export const describeSiteLoadFailure = (
  accountsFailed: number,
  accountsTotal: number,
  detail: string | null = null
): string => {
  if (accountsFailed <= 0) return "";
  const why = detail ? ` (${detail})` : "";
  if (accountsFailed >= accountsTotal)
    return `We could not load your sites${why}.`;
  return `We could not load sites for ${accountsFailed} of ${accountsTotal} accounts${why}, so this list is incomplete.`;
};

/**
 * Loads everything the tabs share and keeps findings in step with it.
 *
 * Effects, each keyed on `refreshToken` so "Run all checks" re-runs them:
 * (1) account-wide data — the parent accounts and every tunnel; (1b) the
 * chosen group's sites and SMTP site ids, loaded only once a group is chosen;
 * (2) the selected site's detail + SMTP instructions; (3) the gap-backed
 * surfaces, which also re-run when the sample switch flips. A final effect
 * recomputes findings whenever any input changes. A timer bumps the token
 * once a minute.
 */
export const useTroubleshootingData = (
  getServerName: (serverId: number | null) => string,
  smtpSummary: FilenameRuleSummary | null
) => {
  const {
    refreshToken,
    requestRefresh,
    scope,
    groupId,
    siteId,
    sampleData,
    sampleScenario,
    sites,
    parentAccounts,
    siteDetail,
    tunnels,
    telemetry,
    siteToSite,
    receivers,
    alertsReceived,
    cameraLastAlarms,
    cameraLiveness,
    savedLoginStatus,
    siteCameras,
    smtpSiteIds,
    setSites,
    setParentAccounts,
    setAccountsLoading,
    setSitesLoading,
    setSitesError,
    setSmtpSiteIds,
    setWireGuardGatewaySiteIds,
    setSmtpAccountIds,
    setTunnels,
    setTunnelsLoading,
    setTunnelsError,
    setSiteDetail,
    setSiteDetailLoading,
    setSmtpInstructions,
    setTelemetry,
    setSiteToSite,
    setReceivers,
    setReceiverTests,
    setAlertsReceived,
    setCameraLastAlarms,
    setCameraLiveness,
    setSavedLoginStatus,
    setSiteCameras,
    setRecordingServers,
    setSiteChanges,
    siteChangesWindow,
    setSiteChangesRefreshing,
    setSiteChangesCompare,
    siteChangesCompare,
    setFindings,
    setLastCheckedAt,
  } = useTroubleshootingStore();

  // (1) account-wide: the accounts to choose from, and every tunnel
  React.useEffect(() => {
    let cancelled = false;
    setAccountsLoading(true);
    setTunnelsLoading(true);
    // A token that lapsed while the page sat open is renewed before anything
    // is requested (see ensureSession); the per-request recovery is the
    // backstop for a token that lapses mid-flight.
    ensureSession().then((ok) => {
      if (cancelled || !ok) return;
      loadAccountWide();
    });
    const loadAccountWide = () => {
      // Only accountsLoading here. Writing sitesLoading from this effect too
      // raced effect (1b): React 17 flushes (1b) between setParentAccounts and
      // the next store write, so its "loading" was cleared while the site
      // request was still in flight and the bar read "0 sites".
      fetchParentAccounts()
        .then((accounts) => {
          if (cancelled) return;
          setParentAccounts(accounts);
          setSitesError("");
          setAccountsLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setParentAccounts([]);
          setSitesError("We could not load your accounts.");
          setAccountsLoading(false);
        });
      // Which groups have SMTP sites, for the SMTP tab's group picker. Unknown
      // (null) means "offer every group", never "offer none".
      fetchSmtpAccountIds()
        .then((ids) => {
          if (!cancelled) setSmtpAccountIds(ids);
        })
        .catch(() => {
          if (!cancelled) setSmtpAccountIds(null);
        });
      withSessionRecovery(fetchAllWireGuardTunnels)
        .then((loaded) => {
          if (cancelled) return;
          // A named sample scenario sets the handshake times (ENG-2039).
          setTunnels(
            sampleData ? SAMPLE_TUNNELS(loaded, sampleScenario) : loaded
          );
          setTunnelsError("");
        })
        .catch(() => {
          if (cancelled) return;
          setTunnels([]);
          setTunnelsError("Failed to load WireGuard tunnels.");
        })
        .finally(() => {
          if (!cancelled) {
            setTunnelsLoading(false);
            setLastCheckedAt(Date.now());
          }
        });
    };
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken, sampleData, sampleScenario]);

  // (1b) the chosen group's sites — nothing is listed until a group is chosen,
  // so this is one request (plus one for its SMTP sites) instead of one per
  // account the user can see.
  React.useEffect(() => {
    const account = parentAccounts.find((a) => a.id === groupId);
    if (groupId == null || !account) {
      setSites([]);
      setSmtpSiteIds([]);
      setWireGuardGatewaySiteIds(null);
      setSitesLoading(false);
      return;
    }
    let cancelled = false;
    setSitesLoading(true);
    fetchTroubleshootingSitesResult([account])
      .then(
        async ({
          sites: loaded,
          accountsTotal,
          accountsFailed,
          failureDetail,
        }) => {
          if (cancelled) return;
          setSites(loaded);
          setSitesError(
            describeSiteLoadFailure(
              accountsFailed,
              accountsTotal,
              failureDetail
            )
          );
          setSitesLoading(false);
          const [smtp, wireGuard] = await Promise.all([
            fetchSmtpSiteIdsResult([groupId]),
            fetchWireGuardGatewaySiteIds(groupId),
          ]);
          if (!cancelled) {
            setSmtpSiteIds(smtp.ids);
            setWireGuardGatewaySiteIds(wireGuard);
          }
        }
      )
      .catch((error) => {
        if (cancelled) return;
        setSites([]);
        setSitesError(
          `We could not load your sites (${describeRequestFailure(error)}).`
        );
        setSitesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, parentAccounts, refreshToken]);

  // (2) one site
  React.useEffect(() => {
    if (scope !== "site" || siteId == null) {
      setSiteDetail(null);
      setSmtpInstructions(null);
      return;
    }
    let cancelled = false;
    setSiteDetailLoading(true);
    Promise.all([
      fetchSiteConnectionDetail(siteId).catch(() => null),
      fetchSmtpInstructions(siteId).catch(() => null),
    ]).then(([detail, instructions]) => {
      if (cancelled) return;
      // A named sample scenario sets the arm state (ENG-2039).
      setSiteDetail(
        detail && sampleData
          ? SAMPLE_SITE_DETAIL(detail, sampleScenario)
          : detail
      );
      setSmtpInstructions(instructions);
      setSiteDetailLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, siteId, refreshToken, sampleData, sampleScenario]);

  // (3) gap-backed surfaces — account-wide and one request each, so they load
  // with or without a group: the Connections, WireGuard and Alarm Receivers
  // tabs work across all groups and narrow when one is chosen.
  React.useEffect(() => {
    let cancelled = false;
    const siteIds = sites.map((s) => s.id);
    fetchSiteToSiteConnections(sampleData, sites).then((r) => {
      if (!cancelled) setSiteToSite(r);
    });
    fetchAlarmReceivers(sampleData, siteIds).then((r) => {
      if (!cancelled) setReceivers(r);
    });
    fetchReceiverTests(sampleData).then((r) => {
      if (!cancelled) setReceiverTests(r);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleData, sampleScenario, refreshToken, sites.length]);

  // A new scope starts with no alert count rather than the previous scope's:
  // findings read the count as this scope's evidence, so a stale zero from
  // another site must not raise a "gone silent" finding here for a moment.
  // The minute refresh keeps the last count while the next one loads.
  React.useEffect(() => {
    setAlertsReceived(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, siteId, scope, sampleData, sampleScenario]);

  // (3b) alerts processed in the last hour, for the scope (ENG-1902). Honours
  // the scope bar: group → group_id, one site → customer_id as well.
  React.useEffect(() => {
    if (groupId == null) {
      setAlertsReceived(null);
      return;
    }
    let cancelled = false;
    const site = scope === "site" && siteId != null ? siteId : null;
    const siteName =
      site != null ? sites.find((s) => s.id === site)?.name ?? null : null;
    fetchAlertsReceived(groupId, site, siteName, sampleData).then((r) => {
      if (!cancelled) setAlertsReceived(r);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, siteId, scope, sampleData, sampleScenario, refreshToken]);

  // (3c) per-camera last alarm, one site only (ENG-1921): the closure check a
  // monitoring centre runs before closing a service ticket.
  React.useEffect(() => {
    if (groupId == null || scope !== "site" || siteId == null) {
      setCameraLastAlarms(null);
      return;
    }
    let cancelled = false;
    setCameraLastAlarms(null);
    fetchCameraLastAlarms(groupId, siteId, sampleData).then((r) => {
      if (!cancelled) setCameraLastAlarms(r);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, siteId, scope, sampleData, sampleScenario, refreshToken]);

  // (3c2) per-camera "working now" signals, one site only (ENG-2046): the
  // reachability evidence. No alerts can just mean nothing moved.
  React.useEffect(() => {
    if (scope !== "site" || siteId == null) {
      setCameraLiveness(null);
      return;
    }
    let cancelled = false;
    setCameraLiveness(null);
    fetchCameraLiveness(siteId, sampleData).then((r) => {
      if (!cancelled) setCameraLiveness(r);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, scope, sampleData, sampleScenario, refreshToken]);

  // (3c2b) the one site's last camera-list refresh (site_status/, on prod
  // today): a recent successful saved login is reachability evidence
  // (ENG-2065). Only the named sample scenarios that need it sample it.
  React.useEffect(() => {
    if (scope !== "site" || siteId == null || sampleData) {
      const sample =
        scope === "site" && sampleData
          ? SAMPLE_SAVED_LOGIN_STATUS(sampleScenario)
          : null;
      setSavedLoginStatus(
        sample && siteId != null ? { siteId, status: sample } : null
      );
      return;
    }
    let cancelled = false;
    setSavedLoginStatus(null);
    fetchSavedLoginStatus(siteId)
      .then((status) => {
        if (!cancelled) setSavedLoginStatus({ siteId, status });
      })
      .catch(() => {
        if (!cancelled) setSavedLoginStatus(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, scope, sampleData, sampleScenario, refreshToken]);

  // (3c3) the one site's cameras with their saved addresses (camera/site/,
  // on prod today): tunnel resolution and the host picker need them even
  // where the liveness endpoint is not deployed.
  React.useEffect(() => {
    if (scope !== "site" || siteId == null) {
      setSiteCameras(null);
      return;
    }
    let cancelled = false;
    setSiteCameras(null);
    fetchSiteCameras(siteId)
      .then((cameras) => {
        if (!cancelled) setSiteCameras(cameras);
      })
      .catch(() => {
        if (!cancelled) setSiteCameras([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, scope, refreshToken]);

  // (3c4) Milestone sites: their recording servers, so the host picker can
  // offer them next to the management and event servers.
  React.useEffect(() => {
    if (
      scope !== "site" ||
      siteId == null ||
      !siteDetail ||
      siteDetail.id !== siteId ||
      !/milestone/i.test(siteDetail.integration_type_name || "")
    ) {
      setRecordingServers(null);
      return;
    }
    let cancelled = false;
    fetchRecordingServers(siteId)
      .then((servers) => {
        if (!cancelled) setRecordingServers(servers);
      })
      .catch(() => {
        if (!cancelled) setRecordingServers([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    siteId,
    scope,
    siteDetail?.id,
    siteDetail?.integration_type_name,
    refreshToken,
  ]);

  // (3d) what we changed on the one site (ENG-1840): settings versions,
  // admin edits and whether the connector picked the newest settings up.
  // The window (ENG-2244) is per site: another site starts at 24 h. A window
  // change refetches only this card and keeps its last answer on screen.
  const changesWindow =
    siteChangesWindow && siteChangesWindow.siteId === siteId
      ? siteChangesWindow.window
      : DEFAULT_CHANGES_WINDOW;
  const changesWindowKey = JSON.stringify(changesWindow);
  const changesBaseKey = `${groupId}|${siteId}|${scope}|${sampleData}|${sampleScenario}|${refreshToken}`;
  const lastChangesBaseKey = React.useRef<string | null>(null);
  React.useEffect(() => {
    const onlyWindowChanged = lastChangesBaseKey.current === changesBaseKey;
    lastChangesBaseKey.current = changesBaseKey;
    if (groupId == null || scope !== "site" || siteId == null) {
      setSiteChanges(null);
      setSiteChangesCompare(null);
      setSiteChangesRefreshing(false);
      return;
    }
    let cancelled = false;
    if (onlyWindowChanged) setSiteChangesRefreshing(true);
    else {
      setSiteChanges(null);
      setSiteChangesCompare(null);
    }
    fetchSiteChanges(siteId, sampleData, changesWindow).then((r) => {
      if (cancelled) return;
      setSiteChanges(r);
      setSiteChangesRefreshing(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changesBaseKey, changesWindowKey]);

  // (3e) one SMTP site: its recent filenames under the saved rule (QA DG-18),
  // the same read the SMTP tab's helper makes, so the filename finding does
  // not wait for someone to open that tab. The tab's own summary, which
  // follows rule edits as they are typed, wins when it is for this site.
  const [smtpBaseline, setSmtpBaseline] =
    React.useState<FilenameRuleSummary | null>(null);
  const smtpScopedSite =
    scope === "site" && siteId != null && smtpSiteIds.includes(siteId)
      ? siteId
      : null;
  React.useEffect(() => {
    setSmtpBaseline(null);
    if (smtpScopedSite == null) return;
    let cancelled = false;
    Promise.all([
      fetchSmtpFilenames(smtpScopedSite, sampleData),
      fetchSiteCameraNames(smtpScopedSite).catch(() => [] as string[]),
    ]).then(([result, cameraNames]) => {
      if (cancelled || result.status !== "ok") return;
      if (result.data.samples.length === 0) return;
      setSmtpBaseline({
        siteId: smtpScopedSite,
        // The sample cameras the SMTP tab substitutes in the preview.
        ...summarizeFilenames(
          result.data,
          sampleData && cameraNames.length === 0
            ? ["ch01", "ch02", "ch03", "ch04"]
            : cameraNames
        ),
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smtpScopedSite, sampleData, sampleScenario, refreshToken]);

  React.useEffect(() => {
    if (tunnels.length === 0) {
      setTelemetry(null);
      return;
    }
    let cancelled = false;
    fetchWireGuardTelemetry(tunnels, sampleData).then((r) => {
      if (!cancelled) setTelemetry(r);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tunnels, sampleData, sampleScenario]);

  // (4) findings
  React.useEffect(() => {
    const scopedTunnels = scopedTunnelsFor(
      scope,
      groupId,
      siteDetail,
      tunnels,
      getAccessibleGroups(),
      siteCameraHosts(cameraLiveness, siteCameras)
    );
    const scopedSites = scopedSitesFor(scope, groupId, siteId, sites);
    const smtpSite =
      scope === "site" && siteId != null
        ? sites.find((s) => s.id === siteId)
        : null;
    // A summary left over from another site says nothing about this one.
    const summary = [smtpSummary, smtpBaseline].find(
      (candidate) => candidate != null && candidate.siteId === smtpSite?.id
    );
    setFindings(
      buildFindings({
        sites: scopedSites,
        siteDetail: scope === "site" ? siteDetail : null,
        tunnels: scopedTunnels,
        telemetry,
        siteToSite,
        receivers,
        smtp:
          smtpSite && summary
            ? {
                siteId: smtpSite.id,
                siteName: smtpSite.name,
                rows: summary.rows,
                counts: summary.counts,
              }
            : null,
        getServerName,
        alertsReceived:
          alertsReceived?.status === "ok" ? alertsReceived.data : null,
        cameraLastAlarms:
          cameraLastAlarms?.status === "ok" ? cameraLastAlarms.data : null,
        cameraLiveness:
          cameraLiveness?.status === "ok" ? cameraLiveness.data : null,
        smtpSiteIds,
        // Only a compare the user already ran; never fetched for this.
        settingsCompare:
          scope === "site" &&
          siteChangesCompare?.siteId === siteId &&
          siteChangesCompare.result?.status === "ok"
            ? siteChangesCompare.result.data
            : null,
        savedLogin: scope === "site" ? savedLoginStatus : null,
      })
    );
  }, [
    siteChangesCompare,
    scope,
    groupId,
    siteId,
    sites,
    siteDetail,
    tunnels,
    telemetry,
    siteToSite,
    receivers,
    alertsReceived,
    cameraLastAlarms,
    cameraLiveness,
    savedLoginStatus,
    siteCameras,
    smtpSiteIds,
    smtpSummary,
    smtpBaseline,
    getServerName,
    setFindings,
  ]);

  // auto refresh
  React.useEffect(() => {
    // A background tab does not refresh: nobody is reading it, and a wall of
    // open Troubleshooter tabs would otherwise re-read every minute each.
    const id = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      requestRefresh();
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [requestRefresh]);
};
